import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createFeatureFlag,
  deleteFeatureFlag,
  getFeatureFlags,
  updateFeatureFlag,
} from '@/api/feature-flag-api'
import { supportKeys } from '@/lib/query-keys'
import type {
  FeatureFlagDTO,
  FeatureFlagKey,
  FeatureFlagRule,
} from '@/schemas/feature-flag.schema'

export function useFeatureFlagManagement() {
  return useQuery({
    queryKey: supportKeys.featureFlags(),
    queryFn: getFeatureFlags,
  })
}
export function useUpdateFeatureFlag() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      key,
      rule,
    }: {
      key: FeatureFlagKey
      rule: FeatureFlagRule
      // Names of the whitelisted companies, known to the editor. The API
      // answers with the rule only.
      companies?: FeatureFlagDTO['companies']
    }) => updateFeatureFlag(key, rule),
    onSuccess: async (saved, { companies }) => {
      // The list holds what is stored. Written here so the editor starts
      // again from the saved rule at once, not when the refetch lands.
      client.setQueryData<{ items: FeatureFlagDTO[] }>(
        supportKeys.featureFlags(),
        (list) =>
          list && {
            items: list.items.map((flag) =>
              flag.key === saved.key
                ? { ...flag, ...saved, companies: companies ?? flag.companies }
                : flag,
            ),
          },
      )
      await client.invalidateQueries({ queryKey: supportKeys.featureFlags() })
      toast.success(
        'Feature flag saved. Users get the change within about an hour, or on reload.',
      )
    },
    onError: (error) =>
      toast.error(
        `Could not save the feature flag: ${error.message}. Your changes have been kept.`,
      ),
  })
}

export function useCreateFeatureFlag() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: async (flag) => {
      await client.cancelQueries({ queryKey: supportKeys.featureFlags() })
      client.setQueryData<{ items: FeatureFlagDTO[] }>(
        supportKeys.featureFlags(),
        (previous) => ({
          items: [
            ...(previous?.items ?? []).filter((item) => item.key !== flag.key),
            flag,
          ],
        }),
      )
      void client.invalidateQueries({ queryKey: supportKeys.featureFlags() })
      toast.success(
        'Feature flag created. Choose its audience when you are ready.',
      )
    },
  })
}

// onDeleted runs before the flag leaves the list: the editor that asked for
// the delete unmounts with it.
export function useDeleteFeatureFlag(onDeleted: () => void) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: async (_deleted, key) => {
      onDeleted()
      client.setQueryData<{ items: FeatureFlagDTO[] }>(
        supportKeys.featureFlags(),
        (list) =>
          list && { items: list.items.filter((flag) => flag.key !== key) },
      )
      await client.invalidateQueries({ queryKey: supportKeys.featureFlags() })
      toast.success('Feature flag deleted.')
    },
    onError: (error) =>
      toast.error(`Could not delete the feature flag: ${error.message}.`),
  })
}
